import React from "react";
import { Grid, Typography } from "@material-ui/core";
import EcosenseLogo from "../../../assets/img/report_logo.png";
import { isCanadaReport } from "../../../utils/reportCountry";
import DefaultCompanyLogo from "../../../assets/img/ic_company.png";
import { apiEndPoint } from "../../../env";
import { AuthUtil } from "../../auth/AuthUtil";

/**
 * Shared header component for all PDF report pages.
 * Left: Company logo (from reportDetails.logoUrl)
 * Right: "Radon Data Analysis By:" + ecosense logo
 */
const ReportPageHeader = ({ reportDetails }) => {
    const accountEmail = reportDetails?.email;
    const isCanada = isCanadaReport(reportDetails);
    const [resolvedCompanyLogoSrc, setResolvedCompanyLogoSrc] =
        React.useState(DefaultCompanyLogo);

    React.useEffect(() => {
        let isMounted = true;

        const resolveLogoSource = async () => {
            if (!accountEmail) {
                if (isMounted) {
                    setResolvedCompanyLogoSrc(DefaultCompanyLogo);
                }
                return;
            }

            try {
                const token = await AuthUtil.getAuthToken();
                const encodedEmail = encodeURIComponent(
                    accountEmail.trim().toLowerCase()
                );
                const response = await fetch(
                    `${apiEndPoint}/company/logo/render?email=${encodedEmail}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: token,
                        },
                        credentials: "include",
                    }
                );

                if (!response.ok) {
                    throw new Error(`Logo render API failed (${response.status})`);
                }

                const json = await response.json();
                const mimeType = json?.data?.mimeType;
                const base64 = json?.data?.base64;

                if (!mimeType || !base64) {
                    throw new Error("Logo render API returned empty payload");
                }

                if (isMounted) {
                    setResolvedCompanyLogoSrc(`data:${mimeType};base64,${base64}`);
                }
            } catch (error) {
                if (isMounted) {
                    setResolvedCompanyLogoSrc(DefaultCompanyLogo);
                }
            }
        };

        resolveLogoSource();

        return () => {
            isMounted = false;
        };
    }, [accountEmail]);

    return (
        <div style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 20 }}>
            <Grid container alignItems="center" style={{ width: "100%" }}>
                {/* Left: Company logo */}
                <Grid item xs={6}>
                    <img
                        src={resolvedCompanyLogoSrc}
                        alt="Company Logo"
                        loading="eager"
                        decoding="sync"
                        style={{
                            height: 50,
                            width: "auto",
                            maxWidth: "100%",
                            display: "block",
                            marginRight: "auto",
                        }}
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = DefaultCompanyLogo;
                        }}
                    />
                </Grid>

                {/* Right: Radon Data Analysis By + ecosense logo */}
                <Grid item xs={6} style={{ textAlign: "right" }}>
                    <Typography
                        style={{
                            color: "#000",
                            fontSize: "10px",
                            fontStyle: "italic",
                            marginBottom: 4,
                        }}
                    >
                        {isCanada ? (
                            <>
                                Radon Data Analysis By/
                                <br />
                                Analyse des donnees sur le radon par :
                            </>
                        ) : (
                            "Radon Data Analysis By:"
                        )}
                    </Typography>
                    <img
                        src={EcosenseLogo}
                        alt="ecosense"
                        loading="eager"
                        decoding="sync"
                        style={{ height: 33, objectFit: "contain" }}
                    />
                </Grid>
            </Grid>

            <div
                style={{
                    marginTop: 0,
                    height: "4px",
                    background: "#575195",
                    borderRadius: 1,
                }}
            />
        </div>
    );
};

export default ReportPageHeader;
